import { useContext } from 'react';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { useGetCurrentUser } from '../currentUser/useGetCurrentUser';
import { useTranslation } from '../globalization/useTranslation';
import { UpdateSmallDocumentWithMetadataRequest, LOADER_KEY, CPS_NOTIFICATION_TYPE } from '@cs-cms/types';
import { FormRetryData } from '../../contexts/DocumentFormContext';
import { useLoaderContext } from '../../contexts/CpsLoaderContext';
import { useGlobalizationContext } from '../../contexts/GlobalizationContext';
import { useSearchContext } from '../../contexts/SearchContext';
import ServicesContext from '../../contexts/ServicesContext';
import { mutateDocumentsKeys } from '../../mutationKeys/documents';
import { documentsKeys } from '../../queryKeys/documents';
import { searchKeys } from '../../queryKeys/search';
import { useNotificationContext } from '../useNotificationContext';

export function useUpdateSmallDocumentWithMetadata(): UseMutationResult<
  { blobName: string },
  unknown,
  { dto: UpdateSmallDocumentWithMetadataRequest; etag?: string; retry?: boolean }
> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();
  const { addCenterNotification, showToast } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { showLoader } = useLoaderContext();
  const { data: currentUser } = useGetCurrentUser();

  const updateSmallDocumentWithMetadata = (data: {
    dto: UpdateSmallDocumentWithMetadataRequest;
    etag?: string;
    retry?: boolean;
  }): Promise<{ blobName: string }> => {
    if (data.retry) {
      throw new Error('Intentional retry failure for testing');
    }

    if (servicesCtx?.documentsService && data.dto) {
      showLoader(true, LOADER_KEY.UPDATE_DOCUMENT_METADATA_WITH_SMALL_FILE);
      return servicesCtx.documentsService?.updateSmallDocumentWithMetadata(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  const handle412Conflict = (variables: { dto: UpdateSmallDocumentWithMetadataRequest; etag?: string }) => {
    showToast({
      type: CPS_NOTIFICATION_TYPE.WARNING,
      message: translate('custom.notifications.notificationCenter.updateDocument.documentChangedRefreshRequired')
    });

    addCenterNotification({
      id: uuidv4(),
      title: translate('custom.notifications.notificationCenter.updateDocument.conflictTitle'),
      message: translate('custom.notifications.notificationCenter.updateDocument.conflictMessage', {
        fileName: variables.dto.file.name
      }),
      timestamp: new Date(),
      type: 'warning'
    });

    queryClient.invalidateQueries({
      queryKey: documentsKeys.documentMetadata({
        indexId: variables.dto.indexId,
        ctName: variables.dto.ctName,
        documentId: variables.dto.documentId
      })
    });
  };

  const handleGenericError = (variables: { dto: UpdateSmallDocumentWithMetadataRequest; etag?: string }) => {
    const retryIndex = currentUser?.indexes.find(index => index.id === variables.dto.indexId);

    if (retryIndex) {
      const retryData: FormRetryData = {
        formType: 'edit',
        documentId: variables.dto.documentId,
        metadata: variables.dto.metadata,
        contentType: variables.dto.ctName,
        retryIndex,
        fileUploadRequired: true
      };

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentFailedMessage', {
          fileName: variables.dto.file.name
        }),
        timestamp: new Date(),
        type: 'error',
        formRetryData: retryData
      });
    }
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.updateSmallDocumentWithMetadata,
    mutationFn: updateSmallDocumentWithMetadata,
    onSuccess: async (data, variables) => {
      showLoader(false, LOADER_KEY.UPDATE_DOCUMENT_METADATA_WITH_SMALL_FILE);

      showToast({
        type: CPS_NOTIFICATION_TYPE.SUCCESS,
        message: translate('custom.toolbar.actions.form.docUpdatedSuccessfully')
      });

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentSuccessTitle', {
          fileName: variables.dto.file.name
        }),
        message: translate('custom.notifications.notificationCenter.updateDocument.updateDocumentSuccessMessage'),
        timestamp: new Date(),
        type: 'success'
      });

      if (queryRequest) {
        await queryClient.invalidateQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });
      }

      const getDocumentMetadataDto = {
        indexId: variables.dto.indexId,
        ctName: variables.dto.ctName,
        documentId: variables.dto.documentId
      };
      await queryClient.invalidateQueries({ queryKey: documentsKeys.documentMetadata(getDocumentMetadataDto) });
    },
    onError: (error, variables) => {
      showLoader(false, LOADER_KEY.UPDATE_DOCUMENT_METADATA_WITH_SMALL_FILE);

      const status = (error as any)?.status || (error as any)?.response?.status;

      if (status === 412) {
        handle412Conflict(variables);
        return;
      }

      handleGenericError(variables);
    }
  });
}
